import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';
import { TooltipItem } from 'chart.js/auto';
import { Doughnut } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    LineElement,
    PointElement,
    LinearScale,
    CategoryScale,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import {
    fetchDeviceCount,
    fetchUserCount,
    fetchAndFilterUsers,
    allUsersData,
    groupByDate,
} from '@utils/utils';

ChartJS.register(
    LineElement,
    PointElement,
    LinearScale,
    CategoryScale,
    Title,
    Tooltip,
    Legend
);
interface SuperAdminChartProps {
    period: string;
}
interface User {
    id: string;
    // other user properties
}

interface UsersData {
    [dateString: string]: User[];
}
const SuperAdminChart: React.FC<SuperAdminChartProps> = ({ period }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const ref = useRef();
    const [individualUserCount, setIndividualUserCount] = useState<
        number | null
    >(null);
    const [hospitalUserCount, setHospitalUserCount] = useState<number | null>(
        null
    );
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [chartData, setChartData] = useState<{
        labels: string[];
        data: number[];
    }>({ labels: [], data: [] });
    useEffect(() => {
        fetchAndFilterUsers()
            .then(({ totalCount, individualCount, hospitalCount }) => {
                setIndividualUserCount(individualCount);
                setHospitalUserCount(hospitalCount);
                setLoading(false); // Set loading to false once data is fetched
            })
            .catch((error) => {
                console.error('Error fetching and filtering users:', error);
                setLoading(false); // Set loading to false if there's an error
            });
        const fetchData = async () => {
            try {
                const usersData = await allUsersData();
                processDataForChart(usersData, period);
            } catch (error: any) {
                setError(error);
            }
        };
        fetchData();
    }, [period]);

    const processDataForChart = (
        usersData: Record<string, any[]>,
        period: string
    ) => {
        const labels: string[] = [];
        const data: number[] = [];

        const today = new Date();
        const currentMonth = today.getMonth();
        const currentYear = today.getFullYear();

        let startDate: Date, endDate: Date;

        // Determine the start and end dates based on the selected period
        switch (period) {
            case 'this_week':
            case 'last_week':
                const startOfWeek = new Date(
                    today.setDate(today.getDate() - today.getDay())
                ); // Start of this week
                if (period === 'last_week') {
                    startOfWeek.setDate(startOfWeek.getDate() - 7); // Move to last week
                }
                startDate = startOfWeek;
                endDate = new Date(startOfWeek);
                endDate.setDate(endDate.getDate() + 6); // End of the week
                break;
            case 'this_month':
                startDate = new Date(currentYear, currentMonth, 1); // Start of this month
                endDate = new Date(currentYear, currentMonth + 1, 0); // End of this month
                break;
            case 'last_month':
                startDate = new Date(currentYear, currentMonth - 1, 1); // Start of last month
                endDate = new Date(currentYear, currentMonth, 0); // End of last month
                break;
            case 'today':
                startDate = new Date(today.setHours(0, 0, 0, 0)); // Start of today
                endDate = new Date(today.setHours(23, 59, 59, 999)); // End of today
                break;
            default:
                return;
        }

        // Define target days or weeks
        const daysOfWeek: string[] = [
            'Sun',
            'Mon',
            'Tue',
            'Wed',
            'Thu',
            'Fri',
            'Sat',
        ];
        const targetDays: (string | number)[] = period.includes('month')
            ? [3, 5, 7, 9, 11, 13, 15, 17, 19, 21, 23, 25, 27, 29, 31] // Numeric days for monthly view
            : daysOfWeek; // Day names for weekly view

        if (period === 'today') {
            const timeSlots: { [key: string]: number } = {};

            // Initialize all hours with 0
            for (let hour = 0; hour < 24; hour++) {
                const timeLabel = `${hour.toString().padStart(2, '0')}:00`;
                timeSlots[timeLabel] = 0;
            }

            // Group data by hour
            Object.entries(usersData).forEach(([dateStr, users]) => {
                const date = new Date(dateStr);
                if (date >= startDate && date <= endDate) {
                    users.forEach((user: any) => {
                        // Use created_at.S from the data
                        const userDate = new Date(
                            user.created_at?.S || dateStr
                        );
                        const hour = userDate.getHours();
                        const timeLabel = `${hour.toString().padStart(2, '0')}:00`;
                        timeSlots[timeLabel] = (timeSlots[timeLabel] || 0) + 1;
                    });
                }
            });

            // Convert time slots to labels and data arrays
            Object.entries(timeSlots)
                .sort((a, b) => parseInt(a[0]) - parseInt(b[0])) // Sort by hour
                .forEach(([timeLabel, count]) => {
                    // Convert 24h to 12h format with AM/PM
                    const hour = parseInt(timeLabel.split(':')[0]);
                    const ampm = hour >= 12 ? 'PM' : 'AM';
                    const hour12 = hour % 12 || 12;
                    const formattedLabel = `${hour12}:00 ${ampm}`;

                    labels.push(formattedLabel);
                    data.push(count);
                });
        } else {
            // Existing logic for other periods
            const sortedDates = Object.keys(usersData)
                .map((dateString) => new Date(dateString))
                .sort((a, b) => a.getTime() - b.getTime());

            for (const date of sortedDates) {
                if (date >= startDate && date <= endDate) {
                    const label = period.includes('month')
                        ? date.getDate().toString().padStart(2, '0')
                        : daysOfWeek[date.getDay()];

                    labels.push(label);
                    data.push(
                        usersData[date.toISOString().split('T')[0]].length
                    );
                }
            }
        }

        // Handle case when no data is available
        if (labels.length === 0) {
            labels.push('');
            data.push(0);
        }

        setChartData({ labels, data });
    };

    const lineData = useMemo(
        () => ({
            labels: chartData.labels,
            datasets: [
                {
                    label: 'User Count',
                    data: chartData.data,
                    fill: true,
                    backgroundColor: 'rgba(67, 121, 238, 0.2)',
                    borderColor: 'rgba(67, 121, 238, 1)',
                    tension: 0.4,
                },
            ],
        }),
        [chartData]
    );
    const lineOptions = useMemo(
        () => ({
            responsive: true,
            scales: {
                y: {
                    grid: {
                        display: true,
                    },
                    beginAtZero: true,
                },
                x: {
                    grid: {
                        display: false,
                    },
                    ticks: {
                        autoSkip:
                            period === 'today'
                                ? true
                                : period === 'this_week' ||
                                    period === 'last_week'
                                  ? false
                                  : true,
                        maxRotation: period === 'today' ? 45 : 0,
                        maxTicksLimit: period === 'today' ? 12 : undefined, // Show fewer ticks for readability
                    },
                },
            },
            plugins: {
                tooltip: {
                    callbacks: {
                        title: (context: any) => {
                            if (period === 'today') {
                                return `Time: ${context[0].label}`;
                            }
                            return context[0].label;
                        },
                    },
                },
            },
        }),
        [period]
    );

    const doughtnudata = {
        labels: ['Individual', 'Hospital'],
        datasets: [
            {
                label: 'User Count',
                data: [individualUserCount || 0, hospitalUserCount || 0], // Use state values
                backgroundColor: ['#4379EE', '#3EAF3F'],
                hoverBackgroundColor: ['#4379EE', '#3EAF3F'],
                cutout: '70%',
            },
        ],
    };

    const options = {
        responsive: true,
        plugins: {
            tooltip: {
                callbacks: {
                    label: function (tooltipItem: TooltipItem<'doughnut'>) {
                        const dataset = tooltipItem.dataset;
                        if (dataset && Array.isArray(dataset.data)) {
                            const label =
                                doughtnudata.labels[tooltipItem.dataIndex]; // Get the label for the current index
                            const value = tooltipItem.raw as number; // Assume raw is a number
                            const total = (dataset.data as number[]).reduce(
                                (sum, val) => sum + val,
                                0
                            ); // Ensure data is an array of numbers
                            const percentage = ((value / total) * 100).toFixed(
                                2
                            ); // Calculate percentage
                            return `${label}: ${percentage}%`; // Return formatted label with percentage
                        }
                        return '';
                    },
                },
                backgroundColor: '#000000', // Set the background color of the tooltip
                titleColor: '#FFFFFF', // Set the title text color of the tooltip
                bodyColor: '#FFFFFF', // Set the body text color of the tooltip
                borderColor: '#FFFFFF', // Optional: Add a border color to the tooltip
                borderWidth: 1, // Optional: Set the border width of the tooltip
            },
            legend: {
                position: 'bottom' as const,
            },
        },
    };

    // Custom plugin to display "No data found" text
    const noDataPlugin = {
        id: 'noDataPlugin',
        beforeDraw: (chart: any) => {
            const { datasets } = chart.data;
            if (datasets[0].data.every((value: number) => value === 0)) {
                const ctx = chart.ctx;
                const width = chart.width;
                const height = chart.height;

                ctx.restore();
                ctx.font = '16px Arial';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillStyle = 'grey';
                ctx.fillText('No data found', width / 2, height / 2);
                ctx.save();
            }
        },
    };

    return (
        <div className="pl-0 flex gap-4 items-center mx-auto 2xl:max-w-[1230px]">
            <div className="cart bg-white flex rounded-2xl h-[381px] w-[760px] p-1 ">
                {/* <img
                    src="/public/images/super_admin_chart1.png"
                    alt="OTP"
                    className="object-contain "
                /> */}
                <Line
                    ref={ref}
                    data={lineData}
                    options={lineOptions}
                    plugins={[noDataPlugin]}
                />
            </div>
            <div className="cart bg-white rounded-2xl flex w-[312px] h-[383px] p-1">
                {/* <img
                    src="/public/images/super_admin_chart2.png"
                    alt="OTP"
                    className="object-contain w-[97%]"
                /> */}
                <div className="doughnut-chart pt-5">
                    <Doughnut
                        data={doughtnudata}
                        options={options}
                        plugins={[noDataPlugin]}
                    />
                </div>
            </div>
        </div>
    );
};

export default SuperAdminChart;
